namespace SolovisGateway.Admin.Users {

  /**
   * AdminUserListControl presents the user interface for administrating environments    
   */

  export class AdminUserListControl extends Solovis.UI.BaseControl {

    private _userList: Array<AdminUserListModel>;
    private _initError;
    private _restrictedAdminUIEnabled: boolean;
    private _hideRequireMfa: boolean;
    private _toolbarContainer;
    private _toolbar: Solovis.UI.Toolbar;
    private _dataGrid: DevExpress.ui.dxDataGrid;

    private _targetWidth: number;
    private _targetHeight: number;

    constructor(options) {
      super(options);
      this._userList = new Array<AdminUserListModel>();
      this._restrictedAdminUIEnabled = options.restrictedAdminUIEnabled ? true : false;
      this._hideRequireMfa = options.hideRequireMfa ? true : false;
      this._targetWidth = options.width ? options.width : null;
      this._targetHeight = options.height ? options.height : 500;
    }

    public async doInitAsync(): Promise<void> {
      let users = await this._getUsers();
      users.forEach(u => this._userList.push(u));
    }

    public doRender() {
      let self = this;

      this._toolbarContainer = $("<div>").appendTo(this.container);

      var toolbar = new Solovis.UI.Toolbar({
        container: this._toolbarContainer
      });
      this._toolbar = toolbar;

      if (!this._restrictedAdminUIEnabled) {
        toolbar.addItem({
          name: "addUserButton",
          createToolbarItem: function () {
            var addUserButton = new Solovis.UI.Buttons.Button({
              element: $("<div>").text("Add User").prepend($("<span>").css("padding-right", "5px").addClass("fa fa-user-plus")),
              size: "small",
              click: function () {
                self._showAddUserInterface({
                  hideRequireMfa: self._hideRequireMfa,
                  saveCallback: function () { self._refreshUsers(); }
                });
              }
            });

            return addUserButton;
          }
        });
      }

      toolbar.addItem({
        name: "addUsersToOrgButton",
        createToolbarItem: function () {
          return new Solovis.UI.Buttons.Button({
            element: $("<div>")
              .text("Add Users to Organization")
              .prepend($("<span>")
                .css("padding-right", "5px")
                .addClass("fa fa-users")),
            size: "small",
            click: function () {
              location.href = Solovis.resolveUrl("~/Admin/UserAdmin/AddUsersToOrganizations")
            }
          });
        }
      });
      // }

      toolbar.addItem({
        name: "userDataManagementButton",
        createToolbarItem: function () {
          return new Solovis.UI.Buttons.Button({
            element: $("<div>")
              .text("User Data Environment Upload")
              .prepend($("<span>")
                .css("padding-right", "5px")
                .addClass("fa fa-users")),
            size: "small",
            click: function () {
              location.href = Solovis.resolveUrl("~/Admin/UserAdmin/UploadUserPermissions");
            }
          });
        }
      });

      // Setup data grid
      let dataGridContainer = $("<div>").appendTo(this.container);

      let store = new DevExpress.data.ArrayStore({
        key: 'id',
        data: this._userList
      });

      dataGridContainer.dxDataGrid({
        dataSource: {
          store: store,
          sort: 'fullName'
        },

        columnResizingMode: "widget",
        columnFixing: { enabled: true },

        allowColumnResizing: true,
        allowColumnReordering: true,
        rowAlternationEnabled: true,
        searchPanel: { visible: true },
        columnChooser: { enabled: true },
        filterRow: {
          visible: true
        },
        export: {
          enabled: true
        },
        groupPanel: {
          visible: true
        },
        scrolling: {
          mode: "virtual"
        },

        width: this._targetWidth,
        height: this._targetHeight,

        columns: [
          {
            caption: "Actions",
            allowSorting: false,
            cellTemplate: function (container, options) {
              let envData = options.data;
              self._buildActionsCell(container, envData);
            },
            width: 75
          },
          {
            dataField: 'fullName',
            dataType: 'string',
            caption: 'Full Name',
            allowSorting: true,
            width: 200
          },
          {
            dataField: 'primaryEmail',
            dataType: 'string',
            caption: !self._restrictedAdminUIEnabled ? 'Primary Email' : "eVestment Username",
            allowSorting: true,
            width: 200,
          },
          {
            dataField: 'contactEmail',
            dataType: 'string',
            caption: 'Contact Email',
            allowSorting: true,
            width: 200,
            visible: self._restrictedAdminUIEnabled
          },
          {
            dataField: 'managingOrganizationName',
            dataType: 'string',
            caption: 'Organization',
            visible: true,
            allowSorting: true,
            width: 150
          },
          {
            dataField: 'isLoginDisabled',
            dataType: 'boolean',
            caption: 'Disabled',
            visible: true,
            allowSorting: true,
            width: 150
          },
          {
            dataField: 'isGatewayAdmin',
            dataType: 'boolean',
            caption: 'Gateway Admin',
            visible: false,
            allowSorting: true,
            width: 150
          },
          {
            dataField: 'id',
            dataType: 'number',
            caption: 'User Id',
            visible: false,
            allowSorting: true,
            width: 150
          },
          {
            dataField: 'code',
            dataType: 'string',
            caption: 'User Code',
            visible: false,
            allowSorting: true,
            width: 150
          },
          {
            dataField: 'managingOrganizationId',
            dataType: 'number',
            caption: 'Organization Id',
            visible: false,
            allowSorting: true,
            width: 150
          },
          {
            dataField: 'requireMfa',
            dataType: 'boolean',
            caption: 'Require MFA',
            visible: false,
            // Auth0 owns MFA once it's fully in control (Auth0 login enabled + legacy disabled) —
            // the in-house RequireMfa flag is moot then, so don't offer it via the column chooser.
            showInColumnChooser: !self._hideRequireMfa,
            allowSorting: true,
            width: 150
          },
          {
            dataField: 'acceptedTermsOfServiceId',
            dataType: 'number',
            caption: 'TOS Accepted Id',
            visible: false,
            allowSorting: true,
            width: 150
          },
          {
            dataField: 'lastLoggedIn',
            dataType: 'date',
            format: "shortDateShortTime",
            caption: 'Last Logged In',
            allowSorting: true,
            width: 150
          }
        ]
      });

      this._dataGrid = dataGridContainer.dxDataGrid("instance");
    }

    private _showAddUserInterface(options) {
      // TODO: Convert to strong reference once everything is TypeScript.
      (SolovisGateway as any).Admin.Users.UserEditorDialog.showDialog(options);
    }


    private _buildActionsCell(container, userData) {
      let columnDiv = container;

      let editUserLink = $("<a>").appendTo(columnDiv);
      $("<span class='fa fa-pencil'>").appendTo(editUserLink);
      editUserLink.attr("title", "Edit User");
      editUserLink.attr(
        "href",
        Solovis.resolveUrl("~/Admin/UserAdmin/Info/" + userData.id));
    }


    private async _refreshUsers(): Promise<void> {
      let self = this;

      let users = await self._getUsers();

      let store = new DevExpress.data.ArrayStore({ key: 'id', data: users });
      self._dataGrid.option("dataSource", store);
    }

    private async _getUsers(): Promise<Array<AdminUserListModel>> {
      let result = await Solovis.Rpc.invokeAndWait("~/Admin/UserAdmin/GetUsers", {});
      if (result && result.result && result.result.users) {
        return result.result.users.map(u => new AdminUserListModel(u));
      }
    }

    public setWidth(newWidth: number) {
      this._targetWidth = newWidth;

      if (this.container) {
        this.container.outerWidth(newWidth);
      }

      if (this._dataGrid) {
        this._dataGrid.option("width", newWidth);
      }
    }

    public setHeight(newHeight: number) {
      this._targetHeight = newHeight;

      if (this.container) {
        this.container.outerHeight(newHeight);
      }

      if (this._dataGrid) {
        this._dataGrid.option("height", newHeight);
      }
    }
  }
}